import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { BoxModelInput } from "@circle-react-shared/BuilderUI/Editor/fields/BoxModelField";
import { Collapsible } from "@circle-react-uikit/Collapsible";
import { Form } from "@circle-react-uikit/Form";
import { Background } from "./Background";
import { Borders } from "./Borders";
import {
  BUTTON_VARIANT_TYPES,
  ButtonStateSelector,
} from "./ButtonStateSelector";
import type { ButtonVariant } from "./ButtonStateSelector";
import { Corners } from "./Corners";
import { Sizing } from "./Sizing";
import { Text } from "./Text";
import { BUTTON_TYPE_OPTIONS } from "./constants";

type ButtonType = "primary" | "secondary" | "custom";

export const generateFormItemName = (
  buttonType: ButtonType,
  buttonVariant: ButtonVariant,
  name: string,
) => {
  const nameParts = name.split(".");
  return ["styles", buttonType, buttonVariant, ...nameParts].join(".");
};

export const StylesEditor = () => {
  const { watch } = useFormContext();

  const [selectedButtonVariant, setSelectedButtonVariant] =
    useState<ButtonVariant>(BUTTON_VARIANT_TYPES.default);

  const currentButtonType = (watch("buttonType") ?? "primary") as ButtonType;

  return (
    <div>
      <Collapsible title={t("builder_ui.prop_type.styles.type")} defaultOpen>
        <div className="px-6 py-5">
          <Form.TextSelect
            options={BUTTON_TYPE_OPTIONS}
            label={null}
            variant="single"
            name="buttonType"
          />
        </div>
      </Collapsible>

      <Sizing buttonType={currentButtonType} />

      <Collapsible title={t("builder_ui.prop_type.styles.padding")} defaultOpen>
        <div className="px-6 pb-5">
          <BoxModelInput
            type="padding"
            name={`styles.${currentButtonType}.padding`}
            label={t("builder_ui.prop_type.styles.padding")}
          />
        </div>
      </Collapsible>

      <Collapsible title={t("builder_ui.prop_type.styles.margin")} defaultOpen>
        <div className="px-6 pb-5">
          <BoxModelInput
            type="margin"
            name={`styles.${currentButtonType}.margin`}
            label={t("builder_ui.prop_type.styles.margin")}
          />
        </div>
      </Collapsible>

      <div key={selectedButtonVariant}>
        <ButtonStateSelector
          selectedButtonVariant={selectedButtonVariant}
          setSelectedButtonVariant={setSelectedButtonVariant}
        />
        <Background
          selectedButtonVariant={selectedButtonVariant}
          buttonType={currentButtonType}
        />
        <Borders
          selectedButtonVariant={selectedButtonVariant}
          buttonType={currentButtonType}
        />
        <Corners
          selectedButtonVariant={selectedButtonVariant}
          buttonType={currentButtonType}
        />
        <Text
          selectedButtonVariant={selectedButtonVariant}
          buttonType={currentButtonType}
        />
      </div>
    </div>
  );
};
